import React, {useState, useEffect} from "react";
import {serverRequest} from "../../../util/server_request";
import { useLocation, useNavigate } from 'react-router-dom';
import {notifyUser} from "../../../util/util";
import {Spin} from "antd";
import { useSendOTP } from "../../../hooks/api/useAuth";
import Navigate from "../../../components/Navigate";

export const OneTimePin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // Always keep E.164 (233XXXXXXXXX) for backend, 0XXXXXXXXX for display
    const rawPhone = location.state?.phone;
    const displayPhone = rawPhone && rawPhone.startsWith('233') && rawPhone.length === 12
        ? '0' + rawPhone.slice(3)
        : rawPhone;
    const backendPhone = rawPhone;

    // Mask phone number for display (e.g., 05****8725)
    const maskPhone = (phone) => {
        if (!phone || phone.length < 6) return phone;
        return phone.slice(0, 2) + '****' + phone.slice(-4);
    };
    // eslint-disable-next-line
    const maskedPhone = maskPhone(displayPhone);
    const employerNumber = location.state?.employerNumber;

    const [otp, setOTP] = useState('');
    const [loading, setLoading] = useState(false);
    const [countdown, setCountdown] = useState(60);
    const [canResend, setCanResend] = useState(false);

    const sendOTPMutation = useSendOTP();

    // Countdown timer for resend OTP
    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
        } else {
            setCanResend(true);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const toggleLoading = () => setLoading(prevState => !prevState);

    const changeHandler = (e) => setOTP(e.target.value);

    // Always use backendPhone for API
    const verifyOTP = () => {
        if (!otp) {
            return notifyUser('error', 'Please enter OTP received to proceed!', 'Validation Error');
        }
        toggleLoading();
        serverRequest('GET', `auth/verify/phone/otp?otp=${otp}&phone=${backendPhone}`)
            .then(result => {
                toggleLoading();
                if (!result?.success) {
                    notifyUser('error', result?.message, 'Error');
                } else {
                    navigate('/onboard/validate-info', { state: { phone: backendPhone, employerNumber } });
                }
            }).catch(error => {
                toggleLoading();
                notifyUser('error', error, 'Error');
            });
    };

    // Resend OTP handler
    const handleResendOTP = () => {
        if (!canResend) return;
        
        // Use displayPhone (0XXXXXXXXX format) for the API as per your curl example
        const phoneToSend = displayPhone || backendPhone;
        
        sendOTPMutation.mutate(
            { phone: phoneToSend },
            {
                onSuccess: () => {
                    // Reset countdown after successful resend
                    setCountdown(60);
                    setCanResend(false);
                },
            }
        );
    };

    // Format countdown as MM:SS
    const formatCountdown = () => {
        const minutes = Math.floor(countdown / 60);
        const seconds = countdown % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div className='mx-auto mobile-full-width shadow bg-white p-5 rounded login-width'>
            <div align='center'>
                <p className='lead fw-bold'>Enter your One Time Pin</p>
                <small className='text-center'>We've sent a One-Time Pin(OTP) to this number <strong>{maskedPhone}</strong>.</small>

                <Spin spinning={loading || sendOTPMutation.isPending}>
                    <div className="mt-4 w-75 mx-auto mobile-full-width">
                        <omk-otp-field
                            number-of-inputs="6"
                            resend-text=""
                            resend-button-label=""
                            label="One-time Password"
                            className='mb-2' value={otp} onInput={changeHandler} />
                        
                        {/* Resend OTP Section */}
                        <div className="mb-3" style={{ paddingBottom: '10px' }}>
                            {!canResend ? (
                                <p className='mb-0 text-muted'>
                                    Resend OTP in <span style={{ fontWeight: 'bold', color: '#009677' }}>{formatCountdown()}</span>
                                </p>
                            ) : (
                                <p className='mb-0' style={{ cursor: 'pointer' }} onClick={handleResendOTP}>
                                    Didn't receive OTP? <span style={{ color: '#009677', fontWeight: 'bold' }}>Resend</span>
                                </p>
                            )}
                        </div>

                        <p className="text-center omk-text-small text-black-50 mb-5"><i className="bi bi-shield-lock"/> Your information is confidential and secure</p>
                        <button className="btn btn-primary mb-4 w-50 mobile-full-width" onClick={verifyOTP}>Continue</button>
                        <p className="text-center mt-3">
                            <Navigate path="/onboard/login">
                                <span 
                                    className="fw-bold text-decoration-underline"
                                    style={{ color: '#009677', cursor: 'pointer' }}
                                >
                                    Back to Login
                                </span>
                            </Navigate>
                        </p>
                    </div>
                </Spin>
            </div>
        </div>
    );
}
